import { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { currentUser } from '../api/auth';
import { fetchPart, PartNotFoundError } from '../api/part';
import AccessDenied from '../components/AccessDenied';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import type { PartBundle, User } from '../api/types';
import Header from '../components/Header';
import PartHeader from '../components/PartHeader';
import PartInvByStoreTable from '../components/PartInvByStoreTable';
import PartEditPanel from '../components/PartEditPanel';
import PartMinMaxPanel from '../components/PartMinMaxPanel';
import PartAuditPanel from '../components/PartAuditPanel';
import PartMovementTimeline from '../components/PartMovementTimeline';
import PartRastreoSection from '../components/PartRastreoSection';
import { t } from '../lib/i18n';

type PartTab = 'summary' | 'rastreo';

export default function Part() {
  const { line, part } = useParams<{ line: string; part: string }>();
  // Tri-state currentUser load: undefined = pending; null = redirect; User = render.
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [bundle, setBundle] = useState<PartBundle | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState<PartTab>('summary');
  // JCD-56 — which store row's edit panel is open, and a counter the panel
  // bumps after a successful save so the history panel refetches.
  const [editSid, setEditSid] = useState<number | null>(null);
  const [auditRefresh, setAuditRefresh] = useState(0);

  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);

  const load = () => {
    if (!user || !line || !part) return;
    let alive = true;
    setErr(null);
    setNotFound(false);
    setBundle(null);
    fetchPart(line, part)
      .then((b) => { if (alive) setBundle(b); })
      .catch((e: unknown) => {
        if (!alive) return;
        if (e instanceof PartNotFoundError) setNotFound(true);
        else setErr(e instanceof Error ? e.message : String(e));
      });
    return () => { alive = false; };
  };
  useEffect(() => load(), [user, line, part]);

  if (user === undefined) return null;
  if (user === null) return <Navigate to="/login" replace />;

  // Accounting is the only role blocked from per-part detail; owner/manager
  // see everything and staff get cost-stripped (handled inside the table).
  const isAuthorized = user.role === 'owner' || user.role === 'manager' || user.role === 'staff';
  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} />
        <main className="flex-1 p-4"><AccessDenied user={user} /></main>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} />
        <main className="flex-1 p-4 space-y-4">
          <EmptyState
            message={t.part.notFound}
            action={<Link to="/parts" className="text-tremor-brand hover:underline">{t.part.back}</Link>}
          />
        </main>
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} />
        <main className="flex-1 p-4"><ErrorRetry message={err} onRetry={load} /></main>
      </div>
    );
  }

  if (!bundle) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} />
        <main className="flex-1 p-4 space-y-4"><TableSkeleton rows={6} cols={4} /></main>
      </div>
    );
  }

  // JCD-56 — the "Editar" action appears only when the write is actually
  // available: writes_enabled on the server AND inventory:edit (owner by
  // default, delegable). The panel re-checks the same pair itself.
  const canEditInv =
    !!user.features?.writes_enabled &&
    (user.role === 'owner' || (user.permissions ?? []).includes('inventory:edit'));

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 p-4 space-y-4 max-w-screen-2xl mx-auto w-full">
        <PartHeader header={bundle.header} />

        <div className="border-b border-tremor-border flex gap-1">
          {(['summary', 'rastreo'] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              className={
                'inline-flex items-center px-3 py-2 text-sm border-b-2 -mb-px ' +
                (tab === k
                  ? 'border-tremor-brand text-tremor-content-strong font-semibold'
                  : 'border-transparent text-tremor-content hover:text-tremor-content-strong')
              }
            >
              {t.part.tabs[k]}
            </button>
          ))}
        </div>

        {tab === 'summary' ? (
          <>
            <PartInvByStoreTable
              inv={bundle.inv_by_store}
              role={user.role}
              onEdit={canEditInv ? (s) => setEditSid((cur) => (cur === s ? null : s)) : undefined}
            />
            {editSid !== null && line && part && (
              <>
                <PartEditPanel
                  sid={editSid}
                  line={line}
                  part={part}
                  user={user}
                  writesEnabled={!!user.features?.writes_enabled}
                  onSaved={() => setAuditRefresh((n) => n + 1)}
                />
                <PartMinMaxPanel
                  sid={editSid}
                  line={line}
                  part={part}
                  user={user}
                  writesEnabled={!!user.features?.writes_enabled}
                  onApplied={() => setAuditRefresh((n) => n + 1)}
                />
                <PartAuditPanel
                  sid={editSid}
                  line={line}
                  part={part}
                  user={user}
                  refreshKey={auditRefresh}
                />
              </>
            )}
            <PartMovementTimeline events={bundle.movement} />
          </>
        ) : (
          line && part && <PartRastreoSection line={line} part={part} />
        )}
      </main>
    </div>
  );
}
